import {
  assertNotEmpty,
  type ConfigurableServer,
  type Optional,
} from '@festina-life/core'
import { useQuery } from '@tanstack/react-query'

import { type ServerServicesType, useService } from '../../../hooks/useService'
import { useNavigationParams } from '../../../system/useNavigationParams'
import { fetchMetaModelKey } from '../keys'
import { transformMetaModel } from './helpers'

const isEnabled = ({
  service,
  branch,
}: {
  service: Optional<ServerServicesType>
  branch: Optional<string>
}) => !!branch && !!service

export const fetcher = async ({
  layerId,
  service,
  server,
  branch,
}: {
  layerId: Optional<string>
  service: Optional<ServerServicesType>
  server: ConfigurableServer
  branch: Optional<string>
}) => {
  if (!isEnabled({ service, branch })) {
    return Promise.resolve(null)
  }

  assertNotEmpty(service)

  try {
    return await (layerId
      ? service.api.ConfigurationMetaService.metaServiceGetGetBranchLayerModel({
          componentName: server.toLocaleLowerCase(),
          branchName: branch!,
          layer: layerId,
        })
      : service.api.ConfigurationMetaService.metaServiceGetGetBranchModel({
          componentName: server.toLocaleLowerCase(),
          branchName: branch!,
        }))
  } catch (e) {
    if (e.status >= 400 && e.status < 500) {
      return null
    } else {
      throw e
    }
  }
}

export const useFetchMetaModel = (layerId: string | null) => {
  const { server, branch } = useNavigationParams()
  const service = useService(server, branch)
  const isFetcherEnabled = isEnabled({ service, branch })

  /* Given that metamodel fetching is done high up in the component tree, we choose not to use susnpense in order to avoid
  suspense boundaries triggering for the full screen.  */
  return useQuery({
    queryKey: isFetcherEnabled
      ? fetchMetaModelKey({
          server,
          branch,
          layerId,
        })
      : [],
    queryFn: () => {
      if (!isFetcherEnabled) {
        return Promise.resolve(null)
      }
      assertNotEmpty(service)

      try {
        return fetcher({ layerId, service, server, branch })
      } catch (e) {
        if (e.status >= 400 && e.status < 500) {
          return null
        } else {
          throw e
        }
      }
    },
    staleTime: Infinity,
    select: transformMetaModel,
  })
}
